import React from 'react'

import { format, isValid, parseISO } from 'date-fns'
import type { MDXComponents } from 'mdx/types'
import Image from 'next/image'
import { HiMiniChevronDown } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import ChangelogVideo from '@/components/changelog/ChangelogVideo'
import CompanyIntelligencePreview from '@/components/changelog/CompanyIntelligencePreview'
import DeepResearchPreview from '@/components/changelog/DeepResearchPreview'
import NewsIntelligencePreview from '@/components/changelog/NewsIntelligencePreview'
import PeopleIntelligencePreview from '@/components/changelog/PeopleIntelligencePreview'

import type { ChangelogEntry } from '@/types/Changelog'

import styles from './styles.module.css'

const ChangelogImage: MDXComponents['img'] = ({
  className,
  src,
  alt,
  title,
}) => {
  if (typeof src !== 'string' || src.length === 0) {
    return null
  }

  return (
    <span
      className={twMerge(
        'relative my-6 block aspect-video w-full overflow-hidden rounded-xl border border-primary-200 bg-primary-100 dark:border-primary-700/50 dark:bg-primary-900/60',
        className,
      )}
    >
      <Image
        src={src}
        alt={alt ?? ''}
        title={title}
        fill={true}
        sizes="(min-width: 768px) 768px, calc(100vw - 32px)"
        className="object-contain"
      />
    </span>
  )
}

const changelogMdxComponents: MDXComponents = {
  h1: ({ className, ...props }) => (
    <h1
      className={twMerge(
        'text-xl md:text-2xl font-bold mt-8 mb-3 first:mt-0 text-primary-900 dark:text-primary-100',
        className,
      )}
      {...props}
    />
  ),
  h2: ({ className, ...props }) => (
    <h2
      className={twMerge(
        'text-lg md:text-xl font-bold mt-8 mb-3 text-primary-900 dark:text-primary-100',
        className,
      )}
      {...props}
    />
  ),
  h3: ({ className, ...props }) => (
    <h3
      className={twMerge(
        'text-base md:text-lg font-bold mt-6 mb-2 text-primary-900 dark:text-primary-100',
        className,
      )}
      {...props}
    />
  ),
  p: ({ className, ...props }) => (
    <p
      className={twMerge(
        'my-3 leading-[1.8] text-primary-600 dark:text-primary-300',
        className,
      )}
      {...props}
    />
  ),
  ul: ({ className, ...props }) => (
    <ul
      className={twMerge('my-4 ml-2 space-y-2 list-none', className)}
      {...props}
    />
  ),
  ol: ({ className, ...props }) => (
    <ol
      className={twMerge('my-4 ml-5 list-decimal space-y-2', className)}
      {...props}
    />
  ),
  li: ({ className, ...props }) => (
    <li
      className={twMerge(
        'text-primary-600 dark:text-primary-300 leading-[1.8] relative pl-5 before:content-["▪"] before:absolute before:left-0 before:text-primary-400 dark:before:text-primary-500',
        className,
      )}
      {...props}
    />
  ),
  a: ({ className, ...props }) => (
    <a
      className={twMerge(
        'text-primary-700 dark:text-primary-200 underline underline-offset-4 decoration-primary-400 dark:decoration-primary-500 hover:opacity-80 transition-opacity',
        className,
      )}
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    />
  ),
  img: ChangelogImage,
  ChangelogVideo,
  CompanyIntelligencePreview,
  DeepResearchPreview,
  NewsIntelligencePreview,
  PeopleIntelligencePreview,
  hr: ({ className, ...props }) => (
    <hr
      className={twMerge(
        'my-8 border-primary-200 dark:border-primary-800',
        className,
      )}
      {...props}
    />
  ),
  code: ({ className, ...props }) => (
    <code
      className={twMerge(
        'px-1.5 py-0.5 rounded bg-primary-100 dark:bg-primary-800/60 text-sm font-mono text-primary-700 dark:text-primary-300',
        className,
      )}
      {...props}
    />
  ),
  pre: ({ className, ...props }) => (
    <pre
      className={twMerge(
        'my-4 p-4 rounded-lg overflow-x-auto bg-primary-900 text-primary-100',
        className,
      )}
      {...props}
    />
  ),
  details: ({ className, ...props }) => (
    <details
      className={twMerge(
        'group border-t border-primary-200 dark:border-primary-800 last:border-b [&_ul]:my-0 [&_ul]:pt-3 [&_ul]:pb-4 [&_ul]:space-y-3 [&_li]:leading-normal [&_li]:before:content-["•"] [&_li]:pl-5',
        className,
      )}
      {...props}
    />
  ),
  summary: ({ className, children, ...props }) => (
    <summary
      className={twMerge(
        'relative py-4 pr-14 text-base font-medium text-primary-700 dark:text-primary-300 cursor-pointer select-none list-none [&::-webkit-details-marker]:hidden',
        className,
      )}
      {...props}
    >
      <span className="block">{children}</span>
      <span
        aria-hidden="true"
        className="absolute right-0 top-1/2 inline-flex size-10 -translate-y-1/2 items-center justify-center text-primary-500 transition-transform duration-300 ease-out group-open:-translate-y-1/2 group-open:rotate-180 motion-reduce:transition-none dark:text-primary-300"
      >
        <HiMiniChevronDown className="size-7" />
      </span>
    </summary>
  ),
}

const TAG_COLORS: Record<string, string> = {
  AI: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
  Pursuit: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  Integrations:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  Platform:
    'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  Improvements:
    'bg-primary-100 text-primary-600 dark:bg-primary-800/60 dark:text-primary-300',
}

const DEFAULT_TAG_COLOR =
  'bg-primary-100 text-primary-700 dark:bg-primary-800/60 dark:text-primary-300'

export const changelogEntryDomId = (entryId: string) =>
  `changelog-entry-${entryId}`

interface ChangelogEntryProps {
  entry: ChangelogEntry
}

export const ChangelogEntryCard: React.FC<ChangelogEntryProps> = ({
  entry,
}) => {
  const publishedAt = parseISO(entry.published_at)
  const formattedDate = isValid(publishedAt)
    ? format(publishedAt, 'MMM d, yyyy')
    : entry.published_at

  return (
    <article
      id={changelogEntryDomId(entry.id)}
      data-changelog-entry-id={entry.id}
      className="scroll-mt-6 py-16 md:py-24 first:pt-0 lg:-mx-40 lg:px-40"
    >
      <div
        data-changelog-entry-anchor
        className="scroll-mt-8 lg:scroll-mt-12 lg:grid lg:grid-cols-[1fr] lg:overflow-visible"
      >
        <div className="mb-4 lg:mb-0 lg:col-start-1 lg:row-start-1 lg:w-28 lg:-ml-40 lg:text-right">
          <p className="text-base text-primary-500 dark:text-primary-400 lg:sticky lg:top-4">
            {formattedDate}
          </p>
        </div>

        <div
          className={twMerge(
            'min-w-0 lg:col-start-1 lg:row-start-1',
            styles.stickyTitleFrame,
          )}
        >
          <div aria-hidden="true" className={styles.stickyTitleProgress} />
          <h2
            className={twMerge(
              'text-3xl md:text-4xl font-normal tracking-tight text-primary-900 dark:text-primary-100 leading-[1.2] lg:sticky lg:top-0 lg:z-10 lg:-mt-4 lg:mb-2 lg:bg-primary-50/95 lg:pt-4 lg:pb-3 dark:lg:bg-default-dark/95',
              styles.stickyTitle,
            )}
          >
            <span className={styles.stickyTitleText}>{entry.title}</span>
          </h2>
          <div className="mb-5">
            {entry.tags.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {entry.tags.map((tag) => (
                  <span
                    key={tag}
                    className={twMerge(
                      'inline-flex items-center px-3 py-1 rounded-md text-sm font-medium',
                      TAG_COLORS[tag] ?? DEFAULT_TAG_COLOR,
                    )}
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
            {entry.summary && (
              <p className="mt-5 text-base md:text-lg leading-[1.8] text-primary-600 dark:text-primary-400">
                {entry.summary}
              </p>
            )}
          </div>

          <div className="text-base">
            <entry.Component components={changelogMdxComponents} />
          </div>
        </div>
      </div>
    </article>
  )
}

export default ChangelogEntryCard
